import {AbilityBuilder, PureAbility} from '@casl/ability';
import {CommentEntry, Company, CompanyArtifact, Roadmap, UserCompanyRole} from '@prisma/client';
import {CompanyRole, GlobalRole} from './roles';
import {createPrismaAbility, PrismaQuery} from "@casl/prisma";
import {DocumentEntity, ProjectEntity} from "@/pb/api/devplan/types/documents";
import {EnterpriseOrganization, User} from "@/pb/api/devplan/types/company";
import {CompanyLimits, UserLimits} from "@/pb/api/devplan/types/usage";
import {defineUserLimitCompanyRules, defineUserLimitGlobalRules} from "@/app/auth/limits";
import {PermissionsProperties} from "@/pb/api/devplan/types/permissions";
import {defineDocumentAbilities} from "@/app/auth/documentAbilities";

export interface CompanySubject {
  id: number
}


type WithCompanyId = {
  companyId?: number
}

type WithDraftPart = WithCompanyId & {
  isDraftPart?: boolean
}

type WithDraftFlag = WithCompanyId & {
  draft?: boolean
}

 
export interface GlobalEntity {
}

export const AllActions = ['manage', 'create', 'read', 'update', 'delete', 'export'] as const

export type Actions = typeof AllActions[number]
export type GlobalSubjects =
  GlobalEntity
  | 'Company'
  | Company
  | 'User'
  | User
  | 'DocumentTemplate'
  | 'ProjectTemplate'
  | 'SuggestionsTemplate'
  | 'UsageTier'
  | 'GlobalConfig'
  | 'WorkLog'
  | 'EnterpriseOrganization'
  | EnterpriseOrganization
  | 'PlgOrganization'
  | 'all'

const allCompanyStringSubjects = [
  'Document',
  'Roadmap',
  'UserCompanyRole',
  'UserInvite',
  'Company',
  'WorkspaceSecret',
  'Project',
  'Artifact',
  'Comment',
  'ChatMessage',
  'RepoMetadata',
  'RepoAnalysis',
  'RepoRescan',
  'SiteRescan',
  'LinearSync',
  'WorkLog',
  'JiraSync',
  'BitBucketSync',
  'ProjectSimulation',
  'RoadmapView',
  'PromptsDownload',
  'PromptsView',
  'FeaturesRegeneration',
  'TasksRegeneration',
  'PulseEnrollment',
  'CodeResearchEnrollment',
  'DevRule',
  'DevConfig',
  'CodeupJob',
  'RunButton',
  'RepoResearchComment',
  'Activity',
  'CatalogueFeature',
  'Signal',
  'Insight',
  'WorkspaceChat',
  'Dashboard',
  'Goal',
  'PersonalApiKey',
  'CompanyRoadmapDefault',
  'Memory',
  'Team',
] as const

const ownerOnlySubjects = [
  'UserCompanyRole',
  'UserInvite',
  'Company',
  'WorkspaceSecret',
  'BitBucketSync',
  'PulseEnrollment',
  'CodeResearchEnrollment',
  'Comment',
  'ChatMessage',
  'WorkspaceChat',
  'Dashboard',
  'CatalogueFeature',
  'Memory',
  // Workspace teams are managed (and read) by workspace admins only in v1.
  // If members later need to see "their team", split read out of this list.
  'Team',
] as const

export type CompanySubjects =
  WithCompanyId
  | 'WithCompanyId'
  | WithDraftPart
  | 'WithDraftPart'
  | WithDraftFlag
  | 'WithDraftFlag'
  | DocumentEntity
  | Roadmap
  | UserCompanyRole
  | CompanySubject
  | ProjectEntity
  | CommentEntry
  | CompanyArtifact
  | typeof allCompanyStringSubjects[number]
  | 'all'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nonOwnerSubjects = allCompanyStringSubjects.filter(s => !ownerOnlySubjects.includes(s as any))

export type GlobalAbility = PureAbility<[Actions, GlobalSubjects], PrismaQuery>
export type CompanyAbility = PureAbility<[Actions, CompanySubjects], PrismaQuery>

export interface UserAbilities {
  global: GlobalAbility
  company: CompanyAbility
}

export function defineGlobalRoleRules(builder: AbilityBuilder<GlobalAbility>, role: GlobalRole) {
  const {can} = builder;
  if (role === GlobalRole.ADMIN) {
    can('manage', 'all');
  }
  if (role === GlobalRole.CUSTOMER) {
    can('create', 'Company')
    can('create', 'EnterpriseOrganization')
    can('create', 'PlgOrganization')
    can('read', 'DocumentTemplate')
    can('read', 'ProjectTemplate')
  }
}

export function defineGlobalUserRules(user: UserWithRoles, builder: AbilityBuilder<GlobalAbility>) {
  const {can} = builder;
  const {id: userId, email, limits} = user
  if (userId) {
    can('manage', 'User', {id: userId})
    can('create', 'WorkLog', {userId})
    can('read', 'DocumentTemplate')
    can('read', 'ProjectTemplate')
  }
  if (email) {
    can('manage', 'User', {email})
  }
  if (limits) {
    defineUserLimitGlobalRules(limits, builder)
  }
}

export function defineOrganizationRules(
  builder: AbilityBuilder<GlobalAbility>,
  orgId: string,
  role: string
) {
  const {can} = builder;

  // OWNER and ADMIN can manage their organization (full access)
  if (role === 'OWNER' || role === 'ADMIN') {
    can('manage', 'EnterpriseOrganization', {id: orgId})
  }
  if (role === 'VIEWER') {
    can('read', 'EnterpriseOrganization', {id: orgId})
  }
}

function defineOrganizationExportRules(
  builder: AbilityBuilder<GlobalAbility>,
  organizationRoles: Record<string, string>,
) {
  const {can, cannot} = builder;

  // Export is intentionally narrower than organization management. Explicitly
  // deny it after wildcard grants (including global admin), then restore it only
  // for users who own the specific organization.
  cannot('export', 'EnterpriseOrganization')
  Object.entries(organizationRoles).forEach(([orgId, role]) => {
    if (role === 'OWNER') {
      can('export', 'EnterpriseOrganization', {id: orgId})
    }
  })
}

const defaultCustomRoles = [
  CompanyRole.PRODUCT, CompanyRole.ENGINEERING, CompanyRole.MARKETING, CompanyRole.PROJECTS_OWNER,
]

function defineCompanyRules(
  user: UserWithRoles,
  builder: AbilityBuilder<CompanyAbility>,
  companyId: number,
  role: CompanyRole,
) {
  const {can} = builder;
  if (role === CompanyRole.OWNER) {
    can('manage', 'all', {companyId});
    can('manage', 'Company', {id: companyId})
  }
  if (role === CompanyRole.EDITOR) {
    can('manage', nonOwnerSubjects, {companyId})
    can('read', 'all', {companyId})
    can('read', 'Company', {id: companyId})
    can('create', ['Comment', 'ChatMessage', 'WorkspaceChat', 'Dashboard', 'Goal'], {companyId})
  }
  if (CompanyRole.VIEWER === role) {
    can('read', 'all', {companyId})
    can('read', 'Company', {id: companyId})
  } else {
    // eslint-disable-next-line
    can('manage', ['FeaturesRegeneration'], {companyId, draft: true} as any)
    can('create', ['Project'], {companyId})
    // eslint-disable-next-line
    can('update', ['Project'], {companyId, draft: true} as any)
  }
  can('create', ['WorkLog', 'Artifact'], {companyId})
  configureCustomRoles(builder, companyId, role)
  const companyLimits = user.companyLimits[companyId]
  const {id: userId} = user
  const limits = companyLimits?.limits ?? user.limits
  if (userId) {
    can('manage', 'Comment', {authorId: userId, companyId})
    can('manage', 'WorkspaceChat', {ownerId: userId, companyId})
    can('manage', 'Dashboard', {createdBy: userId, companyId})
    // eslint-disable-next-line
    can('manage', ['Project'], {companyId, draft: true, authorId: userId} as any)
  }
  if (limits) {
    defineUserLimitCompanyRules(limits, companyId, builder)
  }
}

function configureCustomRoles(
  builder: AbilityBuilder<CompanyAbility>,
  companyId: number,
  role: CompanyRole,
) {
  const {can} = builder;
  if (defaultCustomRoles.includes(role)) {
    can('read', 'all', {companyId})
    can('read', 'Company', {id: companyId})
    can('create', ['Comment'], {companyId})
    can('read', ['ChatMessage'], {companyId})
  }
  if (role === CompanyRole.PROJECTS_OWNER) {
    can('create', ['ChatMessage'], {companyId})
    can(
      'manage',
      [
        'Document',
        'Artifact',
        'Project',
        'Roadmap',
        'FeaturesRegeneration',
        'TasksRegeneration',
        'LinearSync',
        'JiraSync',
      ],
      {companyId},
    )
  }
  if ([CompanyRole.ENGINEERING, CompanyRole.PRODUCT].includes(role)) {
    // Context-specific actions
    can('manage', ['RepoMetadata', 'RepoAnalysis', 'RepoRescan', 'SiteRescan'], {companyId})
    // Sync actions
    can('manage', ['LinearSync', 'JiraSync'], {companyId})
  }
  if (role === CompanyRole.ENGINEERING) {
    can('manage', ['TasksRegeneration'], {companyId})
  }
  if (role === CompanyRole.PRODUCT) {
    // Features are user stories
    can('manage', ['FeaturesRegeneration'], {companyId})
  }
}

export function defineCompanyRulesForGlobal(builder: AbilityBuilder<CompanyAbility>, role: GlobalRole) {
  const {can} = builder;
  if (role === GlobalRole.ADMIN) {
    can('manage', 'all');
  }
}

function defineCompanyRulesForRoles(
  user: UserWithRoles, builder: AbilityBuilder<CompanyAbility>, companyId: number, roles: CompanyRole[]) {
  roles.forEach(r => {
    defineCompanyRules(user, builder, companyId, r)
    defineDocumentAbilities(user, builder, companyId, r)
  })
}

export function buildGlobalAbility(user: UserWithRoles): GlobalAbility {
  const builder = new AbilityBuilder<GlobalAbility>(createPrismaAbility);
  const {globalRoles, organizationRoles} = user
  const isAdmin = globalRoles.includes(GlobalRole.ADMIN)
  user.globalRoles.forEach(r => defineGlobalRoleRules(builder, r))

  // Apply organization role rules (if user has any organization memberships)
  if (organizationRoles) {
    Object.entries(organizationRoles).forEach(([orgId, role]) => {
      defineOrganizationRules(builder, orgId, role)
    })
  }

  defineOrganizationExportRules(builder, organizationRoles ?? {})

  if (!isAdmin) {
    // Admin should have all permissions, no need to process per user items
    defineGlobalUserRules(user, builder)
  }
  return builder.build()
}

function buildCompanyAbility(user: UserWithRoles): CompanyAbility {
  const builder = new AbilityBuilder<CompanyAbility>(createPrismaAbility);
  const {globalRoles} = user;
  const isAdmin = globalRoles.includes(GlobalRole.ADMIN)
  if (!isAdmin) {
    // should not restrict anything for admins
    Object.entries(user.companyRoles).forEach(([id, roles]) => {
      defineCompanyRulesForRoles(user, builder, Number(id), roles)
    })
  }
  user.globalRoles.forEach(r => defineCompanyRulesForGlobal(builder, r))
  return builder.build()
}

export function buildUserAbilities(user: UserWithRoles): UserAbilities {
  return {
    global: buildGlobalAbility(user),
    company: buildCompanyAbility(user),
  }
}

export interface UserWithRoles {
  id?: string
  name?: string | null
  email?: string | null
  image?: string | null
  globalRoles: GlobalRole[]
  companyRoles: { [id: number]: CompanyRole[] }
  organizationRoles?: { [orgId: string]: string }  // OrganizationRole enum values as strings
  permissions: PermissionsProperties
  limits?: UserLimits
  companyLimits: Record<number, CompanyLimits>
}

export function globalAbility(user: UserWithRoles) {
  return buildGlobalAbility(user)
}

export function companyAbility(user: UserWithRoles) {
  return buildCompanyAbility(user)
}
